import { featureCollection } from "@turf/helpers";
import { DEFAULT_DEBOUNCE_PERIOD_MS } from "./Constants/Constants.js";
import { GtfsApiError } from "./Errors/GtfsApiError.js";
import { ATMapboxGeocodingService } from "./Services/Mapbox/ATMapboxGeocodingService.js";
import { GtfsGeocodingService } from "./Services/Gtfs/GtfsGeocodingService.js";
import { ProximitySortingService } from "./Services/ProximitySortingService.js";
import { TypeSortingService } from "./Services/TypeSortingService.js";
import { ValidationService } from "./Services/ValidationService.js";

export class GeocodingClient {
  /** @type {Logger} */
  _logger;
  /** @type {ATMapboxGeocodingService} */
  _mapboxGeocodingService;
  /** @type {GtfsGeocodingService} */
  _gtfsGeocodingService;

  /**
   * Forward geocode a search text query to get results relevant to the query. This function is debounced and will
   * therefore only return a valid result for the latest call when multiple calls are made within the debounce
   * period. All calls that have been "debounced" (not the latest) will resolve the promise with a DebouncedRequestResult.
   * @function GeocodingClient.forwardGeocode
   * @param {ForwardGeocodeOptions} options Forward geocode request options
   * @returns {Promise<GeocodeResult | DebouncedRequestResult>} Full geocode result, or partial geocode result (
   * indicated by the presence of an ErrorResult), or debounced request result (indicating this request was debounced)
   * @throws DataValidationError if the Mapbox response is not valid or did not contain expected values
   * @throws InvalidArgumentError if any of the options given are invalid
   */
  forwardGeocode;

  /**
   * Create the GeocodingClient with the given options.
   * @param {GeocodingClientOptions} options Geocoding client options
   * @throws InvalidArgumentError if any of the options given are invalid
   */
  constructor(options) {
    const { cdnBaseUrl, debouncePeriod, gtfs, limit, logger, mapboxAccessToken } = options;
    let mapbox = options.mapbox; // Separate from others because it may get reassigned if mapboxAccessToken is provided instead

    ValidationService.validateGeocodingClientOptions(options);

    // Support the old configuration options still using mapboxAccessToken
    if (mapboxAccessToken && !mapbox?.accessToken) {
      mapbox = Object.assign({}, { ...mapbox, accessToken: mapboxAccessToken });
    }

    this._logger = logger;
    this._gtfsGeocodingService = new GtfsGeocodingService(gtfs, logger, limit?.gtfs);
    this._mapboxGeocodingService = new ATMapboxGeocodingService(mapbox, cdnBaseUrl, logger, limit?.mapbox);

    this.forwardGeocode = this._debounceAsync(this._forwardGeocode, debouncePeriod ?? DEFAULT_DEBOUNCE_PERIOD_MS);
  }

  /**
   * Forward geocode a search text query to get results relevant to the query.
   * @param {ForwardGeocodeOptions} options Forward geocode request options
   * @returns {Promise<GeocodeResult>} Full geocode result, or partial geocode result (indicated by the presence of an
   *  ErrorResult)
   * @throws InvalidArgumentError if any of the options given are invalid
   */
  async _forwardGeocode({ query, proximity, limit }) {
    try {
      ValidationService.validateForwardGeocodeOptions({ query, proximity, limit });

      // Remove semicolons from the query string as neither Mapbox nor GTFS API allow them
      query = query.replace(";", "");

      let errors = [];
      let resultFeatures = [];

      const [gtfsResults, mapboxResults] = await Promise.all([
        this._gtfsGeocodingService.forwardGeocode({ query, proximity, limit }),
        this._mapboxGeocodingService.forwardGeocode({ query, proximity, limit }),
      ]);

      // Add GTFS results before Mapbox results
      if (gtfsResults.status === "fulfilled") {
        resultFeatures.push(...gtfsResults.value);
      } else {
        const error = gtfsResults.reason;
        if (error instanceof GtfsApiError && error.errorMessages) {
          errors.push(...error.errorMessages);
        } else {
          errors.push(error.toString());
        }
      }

      // Add Mapbox results
      if (mapboxResults.status === "fulfilled") {
        resultFeatures.push(...mapboxResults.value);
      } else {
        errors.push(mapboxResults.reason.toString());
      }

      // Sort all results by proximity, if provided
      // Else, sort by type of location and feature
      if (proximity) {
        resultFeatures = ProximitySortingService.sort(resultFeatures, proximity);
      } else {
        resultFeatures = TypeSortingService.sort(resultFeatures);
      }

      return {
        ...featureCollection(resultFeatures),
        errors: errors.length ? errors : undefined,
      };
    } catch (error) {
      this._logger?.error?.(`Forward geocoder: ${error.name} - ${error.message}`);
      throw error;
    }
  }

  /**
   * Reverse geocode a set of coordinates to get the nearest known location details.
   * @param {Coordinates} coordinates Coordinates as [longitude, latitude]
   * @returns {Promise<GeocodeResult>} Nearest known location details
   * @throws InvalidArgumentError if the coordinates given are invalid
   */
  async reverseGeocode(coordinates) {
    ValidationService.validateReverseGeocodeOptions({ coordinates });

    try {
      const result = await this._mapboxGeocodingService.reverseGeocode(coordinates);
      if (result.status === "fulfilled") {
        return featureCollection(result.value);
      } else {
        return {
          ...featureCollection([]),
          errors: [result.reason.toString()],
        };
      }
    } catch (error) {
      this._logger?.error?.(`Reverse geocoder: ${error.name} - ${error.message}`);
      return {
        ...featureCollection([]),
        errors: [error.toString()],
      };
    }
  }

  /**
   * Debounce an async function. Only the latest call made within the debouncePeriod will be executed. All other previous
   * calls will resolve the returned promise with a DebouncedRequestResult indicating that function call was debounced
   * and a later one will do the actual function execution.
   * @param {function(...[*]): Promise<unknown>} fn Async function to debounce
   * @param debouncePeriod Period of time to wait before the debounced function is run
   * @returns {function(...[*]): Promise<unknown>} The debounced async function
   */
  _debounceAsync(fn, debouncePeriod) {
    let latestResolve;

    return (...args) => {
      return new Promise((resolve, reject) => {
        latestResolve = resolve;
        setTimeout(async () => {
          if (resolve !== latestResolve) {
            resolve({ debounced: true });
          } else {
            try {
              const result = await fn.apply(this, args);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          }
        }, debouncePeriod);
      });
    };
  }
}
